import { connect, FormikValues, getIn } from 'formik'
import { cloneDeep, flatten, sortBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { array, mixed, number, object, string } from 'yup'

import Accordion from '@/components/Accordion/Accordion'
import { Radio, Select, Text } from '@/components/Form'
import AudienceTargeting from '@/components/Form/AudienceTargeting'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import { Column, Row } from '@/components/StyledComponents'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { SecondaryRoundedButton, StyledWandIcon } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { getAppBuilderNotificationSourcesData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  App,
  Feature,
  FieldTypes,
  NotificationSource,
  NotificationViewStepData,
  StepData,
  Triggers,
  TriggerTypes,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'
import { useAppSelector } from '@/store/hooks'

import operationsMessages from './messages'
import messages from './messages'
import notificationMessages from './Notification/messages'
import { StyledRadioInput } from './Notification/NotificationOperation'

//<editor-fold desc="Styled Components">
const StyledColumn = styled(Column)`
  margin-bottom: 4.5rem;
  padding-bottom: 55px;
`
const StyledText = styled(Text)`
  margin-top: 1.6rem !important;
  font-size: 0.9rem;

  &.form__field {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  label {
    grid-column: span 2;
  }

  input {
    width: 5rem;
  }

  input + span {
    margin-left: 0.5rem;
  }
`
const InlineRadioContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 400px;

  && .form__field {
    margin-bottom: 0;
  }
`
const SelectWithPreviewBlock = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: row;
  justify-content: center;
`
const StyledSelect = styled(Select)`
  flex: 1 0 auto;
  max-width: 500px;
  margin: 0.5rem 0 1rem;
`
const EmptyImage = styled.div`
  align-self: end;
  margin-bottom: 16px;
  margin-left: 10px;
  height: 47px;
  width: 47px;
  background-color: #9b9b9b;
  border-radius: 3px;
  color: white;
  display: flex;
  flex-direction: row;
  justify-content: center;

  ::before {
    content: '?';
    margin: auto;
  }
`
const SourceImage = styled.img`
  object-fit: cover;
  border-radius: 3px;
  align-self: end;
  margin-bottom: 16px;
  margin-left: 10px;
  height: 47px;
  width: 47px;
`
const VariableInputContainer = styled.div`
  position: relative;
  p {
    margin: 5px 0;
  }
`
const StyledSectionHeader = styled.div`
  margin: 15px 0;
  font-size: 1.25em;
  font-weight: bold;
`
const StyledAccordionMainTitle = styled.span`
  font-weight: bold;
  font-size: 1rem;
  color: #4b4b4b;
`
const AccordionBody = styled.div`
  margin-left: 15px;
  margin-right: 15px;
`
const StyledSubTitle = styled.span`
  margin-top: 1rem;
  margin-bottom: 5px;
  font-size: 0.9rem;
  font-weight: lighter;
  color: grey;
`
const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  & .field_error {
    display: none;
  }
`
const StyledSubtitleDivider = styled.div`
  border-color: #ededed;
  border-style: solid;
  border-width: 0.025px;
  margin-bottom: 10px;
  width: 100%;
`
const PlaceholderMessage = styled.span`
  color: #565657;
  padding-left: 10px;
`
const StyledBlockMentionsRichTextEditorWithFormik = styled(MentionsRichTextEditorWithFormik)`
  .public-DraftEditor-content {
    min-height: 100px;
  }
`
//</editor-fold>

//<editor-fold desc="Interfaces">
interface NotificationViewProps {
  readOnly?: boolean
  trigger: Triggers
  app: App | undefined
}

enum HiddenBeforeType {
  Default = 'default',
  Variable = 'variable'
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any, inputSchema: any, agentsFlagOn: boolean) => {
  const result: any = {
    data: object().shape({
      title: string().trim().required(intl.formatMessage(notificationMessages.notificationTitleRequired)),
      body: string().trim().required(intl.formatMessage(notificationMessages.notificationBodyRequired)),
      category: string().required('Category is required'),
      hiddenAfter: number()
        .positive(intl.formatMessage(notificationMessages.notificationExpirationPositive))
        .required(intl.formatMessage(notificationMessages.notificationExpirationRequired)),
      hiddenBefore:
        formObject?.data?.isHiddenBefore === HiddenBeforeType.Default || formObject?.data?.isHiddenBefore == null
          ? mixed().notRequired()
          : string().required(intl.formatMessage(notificationMessages.notificationHiddenBeforeRequired)),
      priority: string(),
      audience: array()
        .of(
          object().shape({
            filters: array().of(
              object().shape({
                attribute: object().required().label('Attribute'),
                operator: string().required().label('Operator'),
                value: string().required().label('Value')
              })
            )
          })
        )
        .required('Audience Targeting is required')
        .min(1, 'Audience Targeting is required'),
      correlationId: string().trim(),
      entrypoint: string()
        .trim()
        .required(
          `${
            agentsFlagOn
              ? intl.formatMessage(messages.agentUiViewRequired)
              : intl.formatMessage(messages.appUiViewRequired)
          }`
        ),
      actionLabel: string().trim()
    })
  }
  return result
}

const getInitialValues = (
  stepData: NotificationViewStepData,
  feature: Feature,
  variables: Variable[][],
  app: App,
  agentsFlagOn: boolean
): StepData => {
  return {
    type: 'notification.create',
    data: {
      version: stepData.data?.version,
      correlationId: stepData.data?.correlationId,
      audience: stepData.data?.audience,
      location: stepData.data?.location,
      category: stepData.data?.category,
      notificationSource: stepData.data?.notificationSource || app?.id,
      from: stepData.data?.from || {
        name: app?.name,
        iconUrl: app?.iconUrl
      },
      priority: stepData.data?.priority ?? 'informational',
      hiddenBefore: stepData.data?.hiddenBefore,
      isHiddenBefore: stepData.data?.hiddenBefore == null ? HiddenBeforeType.Default : HiddenBeforeType.Variable,
      // formik does not support boolean types for radio buttons - so translating it
      isHiddenBeforeRelativeToUserTimezone:
        feature.workflow?.trigger.type === TriggerTypes.scheduled &&
        !feature.workflow?.trigger?.triggerData?.isUserSchedule
          ? (stepData.data?.isHiddenBeforeRelativeToUserTimezone?.toString() ?? 'true')
          : 'false',
      hiddenAfter: stepData.data?.hiddenAfter ?? 21,
      entrypoint: stepData.data?.entrypoint,
      title: stepData.data?.title,
      body: stepData.data?.body,
      actionLabel: stepData.data?.actionLabel ?? `${agentsFlagOn ? 'Open agent' : 'Open app'}`
    }
  }
}

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)

  // Check and remove the "isHiddenBefore" form entry (used only to display and work with the radio buttons for hiddenBefore)
  const isHiddenBefore = updatedValues.stepData.data.isHiddenBefore
  if (isHiddenBefore != null) {
    updatedValues.stepData.data.hiddenBefore =
      updatedValues.stepData.data.isHiddenBefore === HiddenBeforeType.Default
        ? undefined
        : updatedValues.stepData.data.hiddenBefore

    delete updatedValues.stepData.data.isHiddenBefore
  }
  // formik does not support boolean types for radio buttons - so translating it from a string back to expected boolean
  updatedValues.stepData.data.isHiddenBeforeRelativeToUserTimezone =
    updatedValues.stepData.data?.isHiddenBeforeRelativeToUserTimezone === 'true'

  return updatedValues
}

// TODO add path to new article
const getNotificationViewOperationHelpUrl = 'https://help.workgrid.com/s/'

const NotificationView = ({ formik, trigger, app, readOnly = false }: NotificationViewProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  const flattenedVariables = flatten(variables)
  const addVariableRef = useRef<any>([])
  const [showSummaryVariablePopOver, setShowSummaryVariablePopOver] = useState(false)
  const [notificationSources, setNotificationSources] = useState<any>([])

  const notificationSourcesFetchState = useAppSelector(getAppBuilderNotificationSourcesData)

  const onChangeHiddenBefore = (to: HiddenBeforeType) => {
    if (to === HiddenBeforeType.Default) {
      formik.setFieldValue('stepData.data.hiddenBefore', undefined)
    }
  }
  useEffect(() => {
    if (app?.iconUrl) {
      const appNotificationSource: NotificationSource = {
        id: app?.id,
        iconUrl: app?.iconUrl,
        isDefault: true,
        title: app?.name,
        connectorInstanceId: '',
        source: '',
        spaceId: ''
      }
      const notificationSources = sortBy(
        notificationSourcesFetchState.notificationSources.concat(appNotificationSource),
        'title'
      )
      setNotificationSources(notificationSources)

      const formikNotificationData = formik.values?.stepData?.data || []
      const selectedNotificationSource = notificationSources?.find(
        (notificationSource: NotificationSource) =>
          formikNotificationData[0]?.notificationSource === notificationSource.id
      )

      const source = selectedNotificationSource || appNotificationSource

      formik.setFieldValue('stepData.data.from', {
        name: source.title,
        iconUrl: source.iconUrl
      })
      if (!selectedNotificationSource) {
        formik.setFieldValue('stepData.data.notificationSource', appNotificationSource.id)
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [app?.id, app?.iconUrl])

  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  return (
    <StyledColumn>
      <Row>
        <StyledSectionHeader>Notification Info</StyledSectionHeader>
      </Row>
      <VariableInput
        readOnly={readOnly}
        required={true}
        variables={variables}
        name={'stepData.data.title'}
        label={intl.formatMessage(notificationMessages.notificationSummaryTitle)}
        placeholder={intl.formatMessage(notificationMessages.notificationSummaryTitlePlaceholder)}
        validVariableTypes={[
          FieldTypes.STRING,
          FieldTypes.BOOLEAN,
          FieldTypes.INTEGER,
          FieldTypes.NUMBER,
          FieldTypes.ANY
        ]}
      />
      <Row>
        {notificationSources.length <= 0 ? (
          <PlaceholderMessage>{'No Notification Sources found'}</PlaceholderMessage>
        ) : (
          <SelectWithPreviewBlock>
            <StyledSelect
              disabled={readOnly}
              name="stepData.data.notificationSource"
              className=""
              onChange={(e: any) => {
                if (e.target.name !== 'stepData.data.notificationSource') return
                const notificationSource = notificationSources.find(
                  (notificationSource: NotificationSource) => e.target.value === notificationSource.id
                )
                formik.setFieldValue('stepData.data.notificationSource', e.target.value)
                formik.setFieldValue('stepData.data.from', {
                  name: notificationSource.title,
                  iconUrl: notificationSource.iconUrl
                })
              }}
              required={true}
              variant={''}
              label={'Send As'}
            >
              {notificationSources.map((notificationSource: NotificationSource) => (
                <Select.Option className="" key={notificationSource.id} value={notificationSource.id}>
                  {notificationSource.title}
                </Select.Option>
              ))}
            </StyledSelect>
            {formik.values.stepData.data?.from?.iconUrl ? (
              <SourceImage src={formik.values.stepData.data?.from?.iconUrl} />
            ) : (
              <EmptyImage />
            )}
          </SelectWithPreviewBlock>
        )}
      </Row>
      <Row className="-mt-4 mb-4">
        <Link className="publisher-back-nav" to="../../../notificationsources">
          {'Manage Sources'}
        </Link>
      </Row>
      <Row>
        <Text
          key="category"
          name="stepData.data.category"
          disabled={readOnly}
          required={true}
          placeholder="Add a category..."
          label="Category"
          onChange={() => {}}
          className={''}
        />
      </Row>
      <Row>
        <Radio
          name="stepData.data.priority"
          label={<FormattedMessage {...messages.ConfigurationStepDefaultPriority} />}
          required={true}
        >
          <StyledInputWrapper>
            <StyledRadioInput
              id="priority-informational"
              name="stepData.data.priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityInformational} />}
              value="informational"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
            <StyledRadioInput
              id="priority-important"
              name="stepData.data.priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityImportant} />}
              value="important"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
            <StyledRadioInput
              id="priority-critical"
              name="stepData.data.priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityCritical} />}
              value="critical"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
          </StyledInputWrapper>
        </Radio>
      </Row>
      <VariableInput
        readOnly={readOnly}
        required={true}
        variables={variables}
        name={'stepData.data.entrypoint'}
        label={agentsFlagOn ? intl.formatMessage(messages.agentUiView) : intl.formatMessage(messages.appUiView)}
        placeholder={intl.formatMessage(messages.featureIdOfView)}
        validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
      />
      <Row>
        <Text
          key="actionLabel"
          name="stepData.data.actionLabel"
          disabled={readOnly}
          required={true}
          placeholder="Add Label for action button"
          label="Action Button Label"
          onChange={() => {}}
          className={'w-full'}
        />
      </Row>
      <Row>
        <StyledSubTitle>{intl.formatMessage(notificationMessages.notificationHiddenBeforeTitle)}</StyledSubTitle>
      </Row>
      <StyledSubtitleDivider />
      {variables?.length > 0 && (
        <>
          <Radio label={intl.formatMessage(notificationMessages.notificationHiddenBefore)}>
            <InlineRadioContainer>
              <Radio.Input
                id={'before-option-default'}
                aria-label={intl.formatMessage(notificationMessages.notificationHiddenBeforeDefault)}
                labelLocation={'after'}
                name={'stepData.data.isHiddenBefore'}
                value={HiddenBeforeType.Default}
                onChange={() => onChangeHiddenBefore(HiddenBeforeType.Default)}
              >
                {intl.formatMessage(notificationMessages.notificationHiddenBeforeDefault)}
              </Radio.Input>
              <Radio.Input
                id={'before-option-variable'}
                aria-label={intl.formatMessage(notificationMessages.notificationHiddenBeforeVariable)}
                labelLocation={'after'}
                name={'stepData.data.isHiddenBefore'}
                value={HiddenBeforeType.Variable}
                onChange={() => onChangeHiddenBefore(HiddenBeforeType.Variable)}
              >
                {intl.formatMessage(notificationMessages.notificationHiddenBeforeVariable)}
              </Radio.Input>
            </InlineRadioContainer>
          </Radio>
          {getIn(formik.values, 'stepData.data.isHiddenBefore') !== HiddenBeforeType.Default && (
            <VariableInputContainer>
              {/* @ts-expect-error */}
              <VariableAutoComplete
                key="notificationHiddenBefore"
                aria-label={intl.formatMessage(notificationMessages.notificationHiddenBeforePlaceholder)}
                readOnly={readOnly}
                required
                placeholder={intl.formatMessage(notificationMessages.notificationHiddenBeforePlaceholder)}
                variables={variables}
                validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
                showValidationMessage={true}
                name={`stepData.data.hiddenBefore`}
                // @ts-expect-error
                onSelectOption={variable => formik.setFieldValue(`stepData.data.hiddenBefore`, variable.name)}
              />
            </VariableInputContainer>
          )}
        </>
      )}
      {trigger.type === TriggerTypes.scheduled && !trigger.triggerData?.isUserSchedule && (
        <Radio label={intl.formatMessage(notificationMessages.notificationHiddenBeforeTimeZone)}>
          <InlineRadioContainer>
            <Radio.Input
              id={'before-option-isHiddenBeforeRelativeToUserTimezone-true'}
              aria-label={intl.formatMessage(notificationMessages.userLocalTimezone)}
              labelLocation={'after'}
              name={'stepData.data.isHiddenBeforeRelativeToUserTimezone'}
              value={'true'}
            >
              {intl.formatMessage(notificationMessages.userLocalTimezone)}
            </Radio.Input>
            <Radio.Input
              id={'before-option-isHiddenBeforeRelativeToUserTimezone-false'}
              aria-label={intl.formatMessage(notificationMessages.utc)}
              labelLocation={'after'}
              name={'stepData.data.isHiddenBeforeRelativeToUserTimezone'}
              value={'false'}
            >
              {intl.formatMessage(notificationMessages.utc)}
            </Radio.Input>
          </InlineRadioContainer>
        </Radio>
      )}
      <StyledText
        className="field__input"
        key="notificationHiddenAfter"
        name="stepData.data.hiddenAfter"
        disabled={readOnly}
        required={true}
        label={intl.formatMessage(notificationMessages.notificationExpiration)}
        isnumberinput="true"
        type="number"
        min={1}
        value={formik.values.stepData.data?.hiddenAfter}
        onChange={formik.setFieldValue}
        data-testid="notification-hidden-after-field"
        afterInput={<span>{intl.formatMessage(notificationMessages.days)}</span>}
      />
      <Accordion
        defaultOpen={false}
        title={
          <StyledAccordionMainTitle>
            {intl.formatMessage(notificationMessages.advancedSettings)}
          </StyledAccordionMainTitle>
        }
      >
        <AccordionBody>
          <Row>
            <StyledSubTitle>{intl.formatMessage(notificationMessages.correlationIdTitle)}</StyledSubTitle>
          </Row>
          <StyledSubtitleDivider />
          <VariableInput
            readOnly={readOnly}
            required={false}
            variables={variables}
            name={'stepData.data.correlationId'}
            label={intl.formatMessage(notificationMessages.correlationId)}
            placeholder={intl.formatMessage(notificationMessages.correlationIdPlaceholder)}
            validVariableTypes={[
              FieldTypes.STRING,
              FieldTypes.BOOLEAN,
              FieldTypes.INTEGER,
              FieldTypes.NUMBER,
              FieldTypes.ANY
            ]}
          />
        </AccordionBody>
      </Accordion>
      <Row>
        <StyledSectionHeader>Audience Targeting</StyledSectionHeader>
      </Row>
      <Row>
        <AudienceTargeting
          readOnly={readOnly}
          required={true}
          variables={variables}
          rulePrefixText={intl.formatMessage(notificationMessages.audienceTargetingRulePrefixText)}
          name="stepData.data.audience"
          title={''}
          startWithAudience={true}
          firstFilterMandatory={true}
        />
      </Row>
      <Row>
        <StyledSectionHeader>Summary Content</StyledSectionHeader>
      </Row>
      <VariableInputContainer>
        <StyledBlockMentionsRichTextEditorWithFormik
          setAddMention={(addMentionFn: any) => (addVariableRef.current['stepData.data.body'] = addMentionFn)}
          name="stepData.data.body"
          required={true}
          formik={formik}
          readOnly={readOnly}
          mentions={getAllFlattenedVariables(flattenedVariables)}
          placeholder="Add content..."
          hasStaticToolBar
        />
        {flattenedVariables.length > 0 && !readOnly && (
          <>
            {showSummaryVariablePopOver && (
              <VariableOverlay
                onSelectVariable={(variable: Variable) => addVariableRef.current['stepData.data.body'](variable)}
                onClose={() => setShowSummaryVariablePopOver(false)}
                variables={variables}
                validVariableTypes={[
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
              />
            )}
            <SecondaryRoundedButton
              onClick={() => {
                setShowSummaryVariablePopOver(true)
              }}
            >
              <>
                <StyledWandIcon />
                <span>{intl.formatMessage(operationsMessages.addVariableButton)}</span>
              </>
            </SecondaryRoundedButton>
          </>
        )}
      </VariableInputContainer>
    </StyledColumn>
  )
}

export default connect(NotificationView)

export { getInitialValues, getValidationSchema, getNetworkRequestObject, getNotificationViewOperationHelpUrl }
